// ============================================================================
// Suggestion Generator — creates contextual SuggestedChip arrays based on
// the current PageContext. Pure functions (no React hooks).
// ============================================================================

import type { SuggestedChip } from "@/lib/api/chat";
import type { PageContext } from "@/hooks/use-page-context";
import type { OnboardingStage } from "@/hooks/use-onboarding";
import {
  ONBOARDING_CHIPS_NEW,
  ONBOARDING_CHIPS_SETUP_COMPLETE,
} from "@/lib/chat/chip-registry";

// ---------------------------------------------------------------------------
// Suggestion template type
// ---------------------------------------------------------------------------

interface SuggestionTemplate {
  /** Display label and query value for the chip */
  label: string;
  value: string;
  /** Optional icon identifier */
  icon?: string;
  /**
   * If set, this suggestion is only shown when the user has access to the
   * given action (i.e. the action is present in the filtered actions list).
   */
  requiredAction?: string;
}

// ---------------------------------------------------------------------------
// Per-page-type suggestion templates
// ---------------------------------------------------------------------------

const SUGGESTION_TEMPLATES: Record<string, SuggestionTemplate[]> = {
  dashboard: [
    {
      label: "Review active epics",
      value: "Take me to my epics and help me review what needs attention",
      icon: "arrow-right",
    },
    {
      label: "How do I get started?",
      value: "Walk me through the one-time SpecTree setup in simple steps.",
      icon: "rocket",
    },
    {
      label: "What agents are available?",
      value: "What SpecTree agents are available and when should I use each one?",
    },
  ],
  "epic-list": [
    {
      label: "Create a new epic",
      value: "Walk me through creating a new epic with the SpecTree agents and explain each step simply.",
      requiredAction: "create-epic",
    },
    {
      label: "Review requests first",
      value: "Take me to epic requests so I can review incoming ideas first",
    },
    {
      label: "Filter this backlog",
      value: "Show me how to filter this epic list to find the right work",
      requiredAction: "filter",
    },
  ],
  "epic-detail": [
    {
      label: "Run this epic",
      value: "Walk me through running this epic with the SpecTree orchestrator and tell me the exact command to use.",
      requiredAction: "execute-epic",
    },
    {
      label: "Review its features",
      value: "Show me the features in this epic and what to look at first",
      requiredAction: "view-description",
    },
    {
      label: "Add a feature",
      value: "Help me add a new feature to this epic",
      requiredAction: "add-feature",
    },
  ],
  "feature-detail": [
    {
      label: "Update feature status",
      value: "Show me how to change the status of this feature",
      requiredAction: "change-status",
    },
    {
      label: "Break into tasks",
      value: "Help me break this feature into clear tasks",
      requiredAction: "add-task",
    },
    {
      label: "Check dependencies",
      value: "Help me review dependencies for this feature",
    },
  ],
  "task-detail": [
    {
      label: "Update this task",
      value: "Show me how to update this task",
      requiredAction: "edit-task",
    },
    {
      label: "Mark progress",
      value: "Help me log progress or change the status of this task",
      requiredAction: "change-status",
    },
    {
      label: "Open parent feature",
      value: "Take me to the parent feature for this task",
    },
  ],
  "team-list": [
    {
      label: "Open a team",
      value: "Help me choose the right team to open from this list",
    },
    {
      label: "Manage members",
      value: "Show me where team members are managed",
    },
  ],
  "team-detail": [
    {
      label: "Manage members",
      value: "Show me how to manage members for this team",
      requiredAction: "manage-members",
    },
    {
      label: "Update settings",
      value: "Take me to the settings I can manage for this team",
      requiredAction: "manage-settings",
    },
  ],
  settings: [
    {
      label: "Change preferences",
      value: "Show me where to update my preferences",
    },
    {
      label: "Review settings",
      value: "Walk me through the settings available on this page",
      requiredAction: "manage-settings",
    },
  ],
  inbox: [
    {
      label: "Review unread updates",
      value: "Help me review unread notifications from this inbox",
    },
    {
      label: "Catch up quickly",
      value: "Summarize the most important recent activity from this inbox",
    },
  ],
  "whats-new": [
    {
      label: "Scan recent changes",
      value: "Highlight the most important recent product changes here",
    },
    {
      label: "Open changelog",
      value: "Take me to the latest changelog details",
    },
  ],
  "epic-request-list": [
    {
      label: "Submit a request",
      value: "Guide me through submitting a new epic request",
      requiredAction: "create-request",
    },
    {
      label: "Review incoming ideas",
      value: "Help me review the epic requests on this page",
    },
    {
      label: "Explain this workflow",
      value: "Explain how epic requests move from proposal to implementation",
    },
  ],
  "epic-request-detail": [
    {
      label: "React to request",
      value: "Show me how to react to this epic request",
      requiredAction: "react",
    },
    {
      label: "Add a comment",
      value: "Help me add a useful comment to this epic request",
      requiredAction: "comment",
    },
  ],
  "admin-users": [
    {
      label: "Manage users",
      value: "Show me where to manage users from this admin page",
      requiredAction: "manage-users",
    },
    {
      label: "Review roles",
      value: "Help me review the user roles available from this page",
    },
  ],
  onboarding: [
    {
      label: "Set up my environment",
      value: "I'm new to SpecTree. Walk me through the setup in simple words and tell me what goes in my normal terminal versus the agent CLI session.",
      icon: "rocket",
    },
    {
      label: "How does this work?",
      value: "Explain the SpecTree workflow — how do epics get created and executed using the SpecTree agents?",
    },
    {
      label: "Available agents",
      value: "What agents does SpecTree have, and when should I use each one?",
    },
  ],
};

/** Maximum number of suggestions to return */
const MAX_SUGGESTIONS = 3;

// ---------------------------------------------------------------------------
// Generator
// ---------------------------------------------------------------------------

/**
 * Generate contextual suggestion chips based on the current page context.
 *
 * 1. Look up templates for the current `pageType`.
 * 2. Filter out templates whose `requiredAction` is not in
 *    `context.availableActions` (already role-filtered upstream).
 * 3. Limit to {@link MAX_SUGGESTIONS}.
 */
export function generateSuggestions(
  context: PageContext,
  onboardingStage?: OnboardingStage,
): SuggestedChip[] {
  // Stage-specific chips override page-type chips for new/setup-complete users
  if (onboardingStage === "new") {
    return ONBOARDING_CHIPS_NEW.slice(0, MAX_SUGGESTIONS);
  }
  if (onboardingStage === "setup-complete") {
    return ONBOARDING_CHIPS_SETUP_COMPLETE.slice(0, MAX_SUGGESTIONS);
  }

  const templates = SUGGESTION_TEMPLATES[context.pageType];
  if (!templates) {
    return [];
  }

  const actionsSet = new Set(context.availableActions);

  return templates
    .filter((t) => {
      if (!t.requiredAction) return true;
      return actionsSet.has(t.requiredAction);
    })
    .slice(0, MAX_SUGGESTIONS)
    .map(
      (t): SuggestedChip => ({
        label: t.label,
        value: t.value,
        ...(t.icon ? { icon: t.icon } : {}),
      }),
    );
}

// ---------------------------------------------------------------------------
// Change detection
// ---------------------------------------------------------------------------

/**
 * Determine whether the context has changed in a meaningful way that
 * warrants regenerating suggestions.
 *
 * A "meaningful change" is a different `pageType` or a different `objectId`
 * (navigating from one epic to another, for example).
 */
export function hasContextChanged(
  prev: PageContext | null,
  next: PageContext,
): boolean {
  if (!prev) return true;
  if (prev.pageType !== next.pageType) return true;
  if (prev.objectId !== next.objectId) return true;
  return false;
}
